import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { TrackId } from '@/constants/studio'

import { ArrowLeft, Check, Drama, Sparkles, Star } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'
import { TRACKS } from '@/constants/studio'

interface ClassesSectionProps {
    onChoose: (track: TrackId) => void
}

const TRACK_ICONS: Record<TrackId, LucideIcon> = {
    'first-stage': Sparkles,
    workshop: Drama,
    youth: Star
}

const ClassesSection: FC<ClassesSectionProps> = ({ onChoose }): ReactNode => {
    return (
        <section
            id='classes'
            aria-labelledby='classes-title'
            className='relative scroll-mt-16 overflow-clip border-y border-border bg-card/30 py-20 sm:py-28'
        >
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 -top-40 mx-auto h-80 w-[min(56rem,100vw)] rounded-full bg-crimson/15 blur-3xl' />

            <div className='relative mx-auto max-w-7xl px-5 sm:px-8'>
                <Reveal className='mx-auto max-w-2xl text-center'>
                    <p className='text-sm font-medium text-primary'>
                        החוגים
                    </p>

                    <h2 id='classes-title' className='mt-3 font-heading text-4xl font-bold leading-tight text-balance sm:text-5xl'>
                        קבוצה לכל גיל ולכל שלב
                    </h2>

                    <p className='mt-5 text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                        הקבוצות מחולקות לפי גיל, כך שכל ילד עובד עם בני גילו ובקצב שמתאים לו. לא בטוחים איזו קבוצה מתאימה? נעזור לכם לבחור.
                    </p>
                </Reveal>

                <ul className='mt-14 grid gap-6 lg:grid-cols-3'>
                    {TRACKS.map((track, index) => {
                        const Icon = TRACK_ICONS[track.id]

                        return (
                            <li key={track.id}>
                                <Reveal delay={index * 120} className='h-full'>
                                    <article className='group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-background/70 p-7 transition-colors duration-500 hover:border-primary/40 sm:p-8'>
                                        <div aria-hidden='true' className='pointer-events-none absolute -top-20 -end-16 size-56 rounded-full bg-primary/10 blur-3xl transition-opacity duration-500 group-hover:opacity-100 sm:opacity-60' />

                                        <div className='flex items-center justify-between gap-4'>
                                            <span className='flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary'>
                                                <Icon aria-hidden='true' className='size-6' />
                                            </span>

                                            <span className='rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1 text-sm font-semibold text-primary'>
                                                {track.grades}
                                            </span>
                                        </div>

                                        <h3 className='mt-6 font-heading text-3xl font-bold'>
                                            {track.name}
                                        </h3>

                                        <p className='mt-3 leading-relaxed text-muted-foreground text-pretty'>
                                            {track.description}
                                        </p>

                                        <ul className='mt-6 space-y-2.5'>
                                            {track.activities.map((activity) => (
                                                <li key={activity} className='flex items-center gap-3 text-sm text-foreground/85'>
                                                    <Check aria-hidden='true' className='size-4 shrink-0 text-primary' />
                                                    {activity}
                                                </li>
                                            ))}
                                        </ul>

                                        <div className='mt-auto pt-8'>
                                            <button
                                                type='button'
                                                onClick={() => onChoose(track.id)}
                                                className='inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-5 text-sm font-semibold text-primary transition-all duration-300 hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                            >
                                                לשיעור ניסיון בקבוצה הזו
                                                <ArrowLeft aria-hidden='true' className='size-4' />
                                            </button>
                                        </div>
                                    </article>
                                </Reveal>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default ClassesSection
